import { useState, useCallback, useRef } from 'react';
import {
  useVapidKey,
  usePushSubscribe,
  useNotificationPreferences,
} from '@/api/queries/notifications';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotification() {
  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

  // The server reports which channels it can actually deliver on. Asking for
  // the VAPID key before that says `push` is available answers 404 on any
  // deployment without a key configured — an error on every page load for a
  // feature nobody enabled.
  const { data: prefData } = useNotificationPreferences();
  const pushAvailable = prefData?.channels?.push ?? false;

  const { data: vapidData } = useVapidKey(isSupported && pushAvailable);
  const subscribeMutation = usePushSubscribe();
  const subscribeMutateRef = useRef(subscribeMutation.mutateAsync);
  subscribeMutateRef.current = subscribeMutation.mutateAsync;
  const [isSubscribing, setIsSubscribing] = useState(false);

  const getPermission = (): NotificationPermission | 'unsupported' => {
    if (!isSupported) return 'unsupported';
    return Notification.permission;
  };

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported || !vapidData?.vapidKey) return false;
    setIsSubscribing(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return false;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.vapidKey).buffer as ArrayBuffer,
      });

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;

      await subscribeMutateRef.current({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });

      return true;
    } catch {
      return false;
    } finally {
      setIsSubscribing(false);
    }
  }, [isSupported, vapidData]);

  return {
    isSupported,
    isSubscribing,
    permission: getPermission(),
    subscribe,
  };
}
